import { useState, useCallback, useEffect, useRef, useContext } from "react";
import { useMatch, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { parsePhoneNumber } from "react-phone-number-input";
import {
  VALIDATION_FIELD_MAPPING,
  APP_SUCCESS_MESSAGES,
  APP_ERROR_MESSAGES,
  DEFAULTS,
  GENERIC_LOOKUP_KEYS,
} from "../../../../utils/appConstant";
import { formatDate, hasResourseAccess } from "../../../../utils/methods";
import { STATUS_CODE } from "../../../../utils/apiConstant";
import { AddEdit as AddEditInterface, AddEditApi, Attachment, DebitCredit, TransactionStatus } from "../../../../interfaces/transaction.interface";
import { Validation } from "../../../../interfaces/common";
import { Modules, NonAuthRoutes, RouteParamsTypes, SortOrder, PermissionModulesEnum, PermissionActionsEnum } from "../../../../enums";
import useFetch from "../../../../hooks/useFetch";
import TransactionService from "../../../../services/transaction.service";
import { AuthContext } from "../../../../contexts/AuthContext";
import PropertyService from "../../../../services/property.service";
import VendorService from "../../../../services/vendor.service";
import ReservationService from "../../../../services/reservation.service";
import CommonService from "../../../../services/common.service";
import UserService from "../../../../services/user.service";
import Toast from "../../../../components/Toast";
import Spinner from "../../../../components/Spinner";
import useUnsavedChangesWarning from "../../../../hooks/useUnsavedChangesWarning";
import ConfirmationFormRouting from "../../../../components/ConfirmationFormRouting";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import { useTranslation } from "react-i18next";
import { validateAddEditForm } from "../validations";
import Form from "../components/Form";
import AttachmentsTab, { UploadQueueItem } from "../components/AttachmentsTab";
import { chunkedUpload } from "../utils/chunkedUpload";

const initialFormData: AddEditInterface = {
  transactionDate: formatDate(new Date()),
  propertyId: "",
  transactionType: "",
  debitCredit: DEFAULTS.debit as DebitCredit,
  reservationId: "",
  vendorId: "",
  paidBy: "",
  carryforwardInvoiceNo: "",
  actualCost: "",
  totalAmount: "0.00",
  hst: DEFAULTS.hst,
  hstAmount: "0.00",
  subTotal: "0.00",
  expenseDetails: "",
  chargeable: true,
  notes: "",
  invoiceId: '',
  invoiceNo: "",
  reviewed: true,
  reviewedBy: "",
  status: DEFAULTS.status as TransactionStatus,
  statusLabel: DEFAULTS.statusLabel,
  attachments: []
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  propertyId: "",
  transactionType: "",
  platform: "",
  reservationId: "",
  vendorId: "",
  paidBy: "",
  reviewedBy: ""
};

interface UseAddEditViewControllerProps {
  paramId?: string;
  mode?: "view" | "edit";
  external?: boolean;
  onClose?: () => void;
}

export const useAddEditViewController = ({ paramId, mode, external, onClose }: UseAddEditViewControllerProps) => {
  const location = useLocation();
  const params = useParams();
  const id = external ? paramId : params.id;
  const viewMode = !!useMatch(Modules.transactions + RouteParamsTypes.view + "/:id");
  const editMode = !!useMatch(Modules.transactions + RouteParamsTypes.edit + "/:id");
  const view = external ? (mode === "view") : viewMode;
  const edit = external ? (mode === "edit") : editMode;
  const navigate = useNavigate();

  const [tab, setTab] = useState<'details' | 'attachments'>('details');
  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [removingAttachmentId, setRemovingAttachmentId] = useState<number | null>(null);
  const [openingAttachmentId, setOpeningAttachmentId] = useState<number | null>(null);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const currentIdRef = useRef<string | undefined>(id);
  const { t } = useTranslation();

  const [shouldBlock, setShouldBlock] = useState(!view);
  const [deleteId, setDeleteId] = useState<string>('');
  const [deleteHasDeleteAccess, setDeleteHasDeleteAccess] = useState<boolean>(false);

  const { permissions, isSystemAdmin } = useContext(AuthContext);
  const hasDeleteAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Transaction]: [PermissionActionsEnum.Delete] }] });

  // Unsaved changes warning
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning({
    when: shouldBlock,
  });

  const { data: allProperties } = useFetch({
    service: PropertyService,
    func: "lookup",
    payload: { searchText: '', ...(!edit && !view && { is_active: '1' }) },
    dependency: [],
  });

  const { data: properties } = useFetch({
    service: PropertyService,
    func: "lookup",
    payload: { searchText: dropdownSearchData.propertyId, ...(!edit && !view && { is_active: '1' }) },
    dependency: [dropdownSearchData.propertyId],
  });

  const { data: allReviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', ...(!edit && !view && { is_active: '1' }) },
    dependency: [],
  });

  const { data: reviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.reviewedBy, ...(!edit && !view && { is_active: '1' }), include: 'properties', include_id: formData.propertyId },
    dependency: [dropdownSearchData.reviewedBy, formData.propertyId],
  });

  const { data: allVendors } = useFetch({
    service: VendorService,
    func: "lookup",
    payload: { searchText: '', ...(!edit && !view && { is_active: '1' }) },
    dependency: [],
  });

  const { data: vendors } = useFetch({
    service: VendorService,
    func: "lookup",
    payload: { searchText: dropdownSearchData.vendorId, ...(!edit && !view && { is_active: '1' }) },
    dependency: [dropdownSearchData.vendorId],
  });

  const { data: allReservations } = useFetch({
    service: ReservationService,
    func: "lookup",
    payload: { searchText: '', propertyId: formData.propertyId },
    dependency: [formData.propertyId],
  });

  const { data: reservations } = useFetch({
    service: ReservationService,
    func: "lookup",
    payload: { searchText: dropdownSearchData.reservationId, propertyId: formData.propertyId },
    dependency: [dropdownSearchData.reservationId, formData.propertyId],
  });

  const { data: allTransactionTypes } = useFetch({
    service: TransactionService,
    func: "transactionTypeslookup",
    payload: { searchText: '' },
    dependency: [],
  });

  const { data: transactionTypes } = useFetch({
    service: TransactionService,
    func: "transactionTypeslookup",
    payload: { searchText: dropdownSearchData.transactionType },
    dependency: [dropdownSearchData.transactionType],
  });

  const { data: allHstOptions } = useFetch({
    service: CommonService,
    func: "statesLookup",
    payload: { searchText: '' },
    dependency: [],
  });

  const { data: hstOptions } = useFetch({
    service: CommonService,
    func: "statesLookup",
    payload: { searchText: dropdownSearchData.hstPercentage },
    dependency: [dropdownSearchData.hstPercentage],
  });

  const { data: allPaidByOptions } = useFetch({
    service: CommonService,
    func: "genericLookup",
    payload: { key: GENERIC_LOOKUP_KEYS.paidBy, searchText: "" },
    dependency: [],
  });

  const { data: paidByOptions } = useFetch({
    service: CommonService,
    func: "genericLookup",
    payload: { key: GENERIC_LOOKUP_KEYS.paidBy, searchText: dropdownSearchData.paidBy },
    dependency: [dropdownSearchData.paidBy],
  });

  useEffect(() => {
    currentIdRef.current = id;

    // Check if we have preserved values from "save and create new" (only when adding, not editing/viewing)
    const preservedDate = !id && location?.state?.transactionDate;
    const preservedPropertyId = !id && location?.state?.propertyId;

    if (preservedDate || preservedPropertyId) {
      setFormData((prev) => ({
        ...initialFormData,
        ...(preservedDate && { transactionDate: preservedDate }),
        ...(preservedPropertyId && { propertyId: preservedPropertyId }),
      }));
    } else {
      setFormData(initialFormData);
    }

    setDropdownSearchData(initialDropdownSearchData)
    setValidation(initialValidation)
    setShouldBlock(!view);
    setUploadQueue([]);
  }, [location.key, id, location.state, view]);

  useEffect(() => {
    setTab(location?.state?.tab ?? 'details');
  }, [location.state, location.key]);

  const { loading: getContentLoading } = useFetch({
    service: TransactionService,
    func: "get",
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: (response: any) => {
      if (!currentIdRef.current) return;

      if (response.transaction_status as TransactionStatus === VALIDATION_FIELD_MAPPING.settled && edit) {
        navigate(Modules.error + NonAuthRoutes.forbidden);
        return;
      }

      setFormData({
        transactionDate: formatDate(new Date(response.date)),
        propertyId: response.property_id,
        transactionType: response.transaction_type_id,
        debitCredit: response.debit_credit,
        reservationId: response.reservation_id || '',
        vendorId: response.vendor_id || '',
        paidBy: response.paid_by || '',
        carryforwardInvoiceNo: response.carryforward_invoice_number,
        invoiceId: response?.invoice?.id ?? "",
        invoiceNo: response?.invoice?.invoice_no ?? "",
        actualCost: response.actual_amount || "",
        totalAmount: response.total_amount ?? 0,
        hst: response.hst_percentage ? String(response.hst_percentage) : '',
        hstAmount: response.hst_amount,
        subTotal: response.sub_total_amount,
        expenseDetails: response.transaction_details,
        chargeable: response.chargeable,
        notes: response.notes,
        reviewed: response.reconciled,
        reviewedBy: response.reconciled_by,
        status: response.transaction_status,
        statusLabel: response.transaction_status_label,
        attachments: response.attachments || [],
      });
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.transactions);
    }
  });

  const handleInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.phoneNumber) {
      const phoneNumber = parsePhoneNumber(event?.target?.value ?? "");

      setFormData((prev) => ({
        ...prev,
        [VALIDATION_FIELD_MAPPING.phoneNumber]: phoneNumber && phoneNumber.nationalNumber,
        [VALIDATION_FIELD_MAPPING.countryCode]: phoneNumber ? `+${phoneNumber && phoneNumber.countryCallingCode}` : ""
      }));
    } else if (name === VALIDATION_FIELD_MAPPING.totalAmount || name === VALIDATION_FIELD_MAPPING.subTotal || name === VALIDATION_FIELD_MAPPING.hst) {
      setFormData((prev) => {
        const totalAmount = (name === VALIDATION_FIELD_MAPPING.totalAmount ? Number(event.target.value) : Number(prev.totalAmount));
        const subTotal = (name === VALIDATION_FIELD_MAPPING.subTotal ? Number(event.target.value) : Number(prev.subTotal));
        const hst = (name === VALIDATION_FIELD_MAPPING.hst ? Number(event.target.value) : Number(prev.hst));

        let calculatedValues = {};
        if (name === VALIDATION_FIELD_MAPPING.totalAmount) {
          const calculatedSubTotal = (totalAmount || 0) / (1 + hst / 100)
          calculatedValues = {
            [VALIDATION_FIELD_MAPPING.subTotal]: calculatedSubTotal.toFixed(2),
            [VALIDATION_FIELD_MAPPING.hstAmount]: ((totalAmount || 0) - (calculatedSubTotal || 0)).toFixed(2)
          }
        } else if (name === VALIDATION_FIELD_MAPPING.subTotal) {
          const calculatedHstAmount = (subTotal || 0) * hst / 100
          calculatedValues = {
            [VALIDATION_FIELD_MAPPING.hstAmount]: calculatedHstAmount.toFixed(2),
            [VALIDATION_FIELD_MAPPING.totalAmount]: ((subTotal || 0) + (calculatedHstAmount || 0)).toFixed(2)
          }
        } else if (name === VALIDATION_FIELD_MAPPING.hst) {
          const hstValue = Number(event.target.value);
          if (!hstValue) {
            calculatedValues = {
              [VALIDATION_FIELD_MAPPING.hstAmount]: '0.00',
              [VALIDATION_FIELD_MAPPING.totalAmount]: '',
              [VALIDATION_FIELD_MAPPING.subTotal]: subTotal.toFixed(2),
            };
          } else {
            const calculatedHstAmount = (subTotal || 0) * hstValue / 100;
            const calculatedTotalAmount = (subTotal || 0) + calculatedHstAmount;
            calculatedValues = {
              [VALIDATION_FIELD_MAPPING.hstAmount]: calculatedHstAmount.toFixed(2),
              [VALIDATION_FIELD_MAPPING.totalAmount]: calculatedTotalAmount.toFixed(2),
            };
          }
        }

        return {
          ...prev,
          ...calculatedValues,
          [name]: event.target.value,
        };
      });
    } else if (name === VALIDATION_FIELD_MAPPING.propertyId) {
      const selectedProperty = (allProperties || []).find((p: any) => String(p.id) === String(event.target.value));
      setFormData((prev) => ({
        ...prev,
        [VALIDATION_FIELD_MAPPING.reviewedBy]: "",
        [name]: event.target.value,
        hst: selectedProperty?.primary_tax_percentage != null ? String(selectedProperty.primary_tax_percentage) : '',
      }));
    } else {
      let value: any;

      if ([VALIDATION_FIELD_MAPPING.chargeable, VALIDATION_FIELD_MAPPING.reviewed].includes(name)) {
        value = !!event.target.checked;
      } else {
        value = event.target.value;
      }

      setFormData((prev) => {
        return {
          ...prev,
          [name]: value
        };
      });
    }
  }, [allProperties]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const handleInputBlur = useCallback((event: any, name: string) => {
    let newValidation = validateAddEditForm({
      ...formData,
      [name]: event.target.value.trim(),
    }, name);
    setValidation(prev => {
      const filteredFields = prev.fields.filter(f => f.field !== name);
      const mergedFields = [...filteredFields, ...newValidation.fields];

      return {
        validate: mergedFields.length === 0,
        fields: mergedFields,
      };
    });
  }, [formData]);

  const handleTabChange = useCallback((_e: any, value: 'details' | 'attachments') => {
    setTab(value);
  }, []);

  const startUpload = useCallback((localId: string, file: File, transactionId: string) => {
    setUploadQueue((prev) => prev.map((item) => (
      item.localId === localId ? { ...item, status: 'uploading', error: undefined } : item
    )));

    chunkedUpload(file, transactionId, {
      onProgress: (uploadedChunks, totalChunks) => {
        setUploadQueue((prev) => prev.map((item) => (
          item.localId === localId ? { ...item, uploadedChunks, totalChunks } : item
        )));
      },
      onComplete: (attachment: Attachment) => {
        setUploadQueue((prev) => prev.filter((item) => item.localId !== localId));
        setFormData((prev) => ({ ...prev, attachments: [...(prev.attachments || []), attachment] }));
      },
      onError: (error: any) => {
        setUploadQueue((prev) => prev.map((item) => (
          item.localId === localId ? { ...item, status: 'error', error: error?.message } : item
        )));
      },
    });
  }, []);

  const handleFilesSelected = useCallback((fileList: FileList) => {
    if (!id) return;

    const newItems: UploadQueueItem[] = Array.from(fileList).map((file) => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      status: 'uploading',
      uploadedChunks: 0,
      totalChunks: 1,
    }));

    setUploadQueue((prev) => [...prev, ...newItems]);
    newItems.forEach((item) => startUpload(item.localId, item.file, id));
  }, [id, startUpload]);

  const handleRetryQueueItem = useCallback((localId: string) => {
    if (!id) return;
    const item = uploadQueue.find((q) => q.localId === localId);
    if (item) startUpload(localId, item.file, id);
  }, [id, uploadQueue, startUpload]);

  const handleRemoveQueueItem = useCallback((localId: string) => {
    setUploadQueue((prev) => prev.filter((item) => item.localId !== localId));
  }, []);

  const handleRemoveAttachment = useCallback(async (attachmentId: number) => {
    if (!id) return;

    setRemovingAttachmentId(attachmentId);
    try {
      await TransactionService.removeAttachment(id, attachmentId);
      setFormData((prev) => ({
        ...prev,
        attachments: (prev.attachments || []).filter((a) => a.id !== attachmentId),
      }));
    } catch (e) {
      toast.error(<Toast message={APP_ERROR_MESSAGES.transactions.addEdit.removeAttachmentFailed} />, { icon: false });
    } finally {
      setRemovingAttachmentId(null);
    }
  }, [id]);

  const handleOpenAttachment = useCallback(async (attachment: Attachment) => {
    if (!id) return;

    setOpeningAttachmentId(attachment.id);
    try {
      const response: any = await TransactionService.getAttachmentDownloadUrl(id, attachment.id);
      const link = document.createElement('a');
      link.href = response.url;
      link.download = attachment.originalFilename;
      link.rel = 'noreferrer';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(<Toast message={APP_ERROR_MESSAGES.transactions.addEdit.openAttachmentFailed} />, { icon: false });
    } finally {
      setOpeningAttachmentId(null);
    }
  }, [id]);

  const handleSubmit = useCallback(async (action: "1" | "2", event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateAddEditForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    if (action === "1") setLoading(true);
    else setActionTwoLoading(true);

    try {
      const payload: AddEditApi = {
        date: formData.transactionDate,
        property_id: formData.propertyId,
        debit_credit: formData.debitCredit,
        transaction_type_id: formData.transactionType,
        reservation_id: formData.reservationId,
        carryforward_invoice_number: formData.carryforwardInvoiceNo,
        vendor_id: formData.vendorId,
        paid_by: formData.paidBy || null,
        transaction_details: formData.expenseDetails,
        actual_amount: formData.actualCost,
        total_amount: formData.totalAmount,
        hst_percentage: formData.hst,
        hst_amount: formData.hstAmount,
        sub_total_amount: formData.subTotal,
        notes: formData.notes,
        chargeable: formData.chargeable,
        reconciled: formData.reviewed,
        reconciled_by: formData.reviewedBy,
        transaction_status: formData.status,
      };

      let response: any;
      if (!edit) {
        response = await TransactionService.add(payload);
      }
      else if (id) {
        response = await TransactionService.edit(id, payload);
      }

      if (response?.success !== false) {
        toast.success(<Toast message={id ? APP_SUCCESS_MESSAGES.transactionUpdated : APP_SUCCESS_MESSAGES.transactionCreated} />, { icon: false });

        setShouldBlock(false);
        setTimeout(() => {
          if (external) {
            onClose && onClose();
          } else {
            if (action === "2") {
              window.scrollTo({ top: 0, behavior: "smooth" });
              const input = document.getElementById(VALIDATION_FIELD_MAPPING.transactionDate);
              input && input.focus();
              navigate(Modules.transactions + RouteParamsTypes.add, {
                replace: true,
                state: {
                }
              });
            } else if (!edit && response?.id) {
              // First save of a new transaction — attachments can only be
              // uploaded once the transaction has an ID, so land on its
              // edit view with the Attachments tab selected instead of the list.
              navigate(`${Modules.transactions}${RouteParamsTypes.edit}/${response.id}`, {
                replace: true,
                state: { tab: 'attachments' },
              });
            } else {
              navigate(Modules.transactions);
            }
          }
        }, 0);
      }

      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    } catch (e) {
      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    }
  }, [id, edit, formData, external, navigate, onClose]);

  const handleKeyDown = useCallback((e: any, name?: string) => {
    if (name && [VALIDATION_FIELD_MAPPING.expenseDetails, VALIDATION_FIELD_MAPPING.notes].includes(name)) return null
    if (e.key === 'Enter') handleSubmit('1');
  }, [handleSubmit]);

  const handleCancel = useCallback(() => {
    setShouldBlock(false);
    setTimeout(() => {
      if (external) {
        onClose && onClose();
      } else {
        navigate(Modules.transactions);
      }
    }, 0);
  }, [external, navigate, onClose]);

  let formContent;
  if (getContentLoading) {
    formContent = (
      <div className='card-header text-center'>
        <Spinner />
      </div>
    )
  } else if (tab === 'attachments') {
    formContent = (
      <AttachmentsTab
        attachments={formData.attachments || []}
        uploadQueue={uploadQueue}
        view={view}
        removingId={removingAttachmentId}
        openingId={openingAttachmentId}
        onFilesSelected={handleFilesSelected}
        onRemoveAttachment={handleRemoveAttachment}
        onOpenAttachment={handleOpenAttachment}
        onRetryQueueItem={handleRetryQueueItem}
        onRemoveQueueItem={handleRemoveQueueItem}
      />
    )
  } else {
    formContent = (
      <Form
        formData={formData}
        validation={validation}
        view={view}
        edit={edit}
        allProperties={allProperties}
        properties={properties}
        allReservations={allReservations}
        reservations={reservations}
        allVendors={allVendors}
        vendors={vendors}
        allPaidByOptions={allPaidByOptions}
        paidByOptions={paidByOptions}
        allTransactionTypes={allTransactionTypes}
        transactionTypes={transactionTypes}
        allHstOptions={allHstOptions}
        hstOptions={hstOptions}
        allReviewedByOptions={allReviewedByOptions}
        reviewedByOptions={reviewedByOptions}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
    )
  }

  const handleDeleteClick = () => {
    setDeleteHasDeleteAccess(hasDeleteAccess);
    setDeleteId(id || '');
  };

  const handleDeleteTransaction = useCallback(async () => {
    try {
      const response: any = await TransactionService.deleteTransaction(deleteId);
      if (response?.success !== false) {
        toast.success(<Toast message={deleteHasDeleteAccess ? APP_SUCCESS_MESSAGES.transactionDeleted : APP_SUCCESS_MESSAGES.transactionDeleteRequestSent} />, { icon: false });
        setShouldBlock(false);
        setTimeout(() => navigate(Modules.transactions), 0);
      }
      setDeleteId('');
    } catch (e) {
      setDeleteId('');
    }
  }, [deleteId, deleteHasDeleteAccess, navigate]);

  const deleteModalAction = deleteHasDeleteAccess ? (
    <div className='d-flex gap-3'>
      <Button
        className="btn btn-outline-secondary"
        text={t("transactions.list.pageTexts.table.confirmModal.cancelBtn")}
        onClick={() => setDeleteId('')}
      />
      <Button
        className="btn btn-danger"
        text={t("transactions.list.pageTexts.table.confirmModal.deleteBtn")}
        onClick={handleDeleteTransaction}
      />
    </div>
  ) : (
    <div className='d-flex gap-3'>
      <Button
        className="btn btn-outline-secondary"
        text={t("transactions.list.pageTexts.table.confirmModal.cancelBtn")}
        onClick={() => setDeleteId('')}
      />
      <Button
        className="btn btn-primary"
        text={t("transactions.list.pageTexts.table.confirmModal.sendRequestBtn")}
        onClick={handleDeleteTransaction}
      />
    </div>
  );

  const deleteModal = (
    <Modal
      open={deleteId ? true : false}
      onClose={() => setDeleteId('')}
      title={t("transactions.list.pageTexts.table.confirmModal.title")}
      content={
        deleteHasDeleteAccess
          ? t("transactions.list.pageTexts.table.confirmModal.content")
          : t("transactions.list.pageTexts.table.confirmModal.requestContent")
      }
      action={deleteModalAction}
      contentClassName="text-center"
      maxWidth="sm"
    />
  );

  const unsavedChangesModal = (
    <ConfirmationFormRouting
      open={showPrompt}
      onClose={cancelNavigation}
      onConfirm={confirmNavigation}
    />
  );

  return {
    id,
    tab,
    handleTabChange,
    formData,
    dropdownSearchData,
    loading,
    actionTwoLoading,
    validation,
    allProperties,
    properties,
    allReviewedByOptions,
    reviewedByOptions,
    allVendors,
    vendors,
    allPaidByOptions,
    paidByOptions,
    allReservations,
    reservations,
    allTransactionTypes,
    transactionTypes,
    allHstOptions,
    hstOptions,
    getContentLoading,
    view,
    edit,
    formContent,
    unsavedChangesModal,
    setFormData,
    setDropdownSearchData,
    handleInputChange,
    handleDropdownSearchInputChange,
    handleInputBlur,
    handleSubmit,
    handleKeyDown,
    handleCancel,
    handleDeleteClick,
    deleteModal,
  };
};
